import { useState } from 'react';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormHelperText,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, isValid, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { FieldArrayRenderProps } from 'formik';
import { isEmpty } from 'lodash';
import ConfirmLeaveDialog from 'src/components/ConfirmLeaveDialog';

import * as S from '../../style';

const CertificationForm = ({
  open,
  onClose,
  formik,
  index,
  arrayHelpers,
  actionType,
  handleUpdate,
}: {
  open: boolean;
  onClose: () => void;
  formik: any;
  index: number;
  arrayHelpers: FieldArrayRenderProps;
  actionType: 'create' | 'update' | 'delete' | undefined;
  handleUpdate: (values: any) => void;
}) => {
  const [openLeaveDialog, setOpenLeaveDialog] = useState<boolean>(false);
  const handleCancel = () => {
    if (actionType === 'create') {
      arrayHelpers.pop();
    } else if (actionType === 'update') {
      formik.handleReset();
    }
    onClose();
  };

  const handleSave = async () => {
    const validationResult = await formik.validateForm();

    if (isEmpty(validationResult)) {
      handleUpdate(formik.values.certifications);
      onClose();
    }
  };

  const getErrors = <T extends { [key: string]: any }>(
    fieldName: keyof T,
    index: number,
  ) => {
    const error = formik.errors.certifications?.[index] as T | undefined;
    return error ? error[fieldName] : '';
  };

  return (
    <Dialog
      open={open}
      maxWidth={false}
      sx={{
        maxWidth: 750,
        margin: '0 auto',
        '& .MuiPaper-root': { width: '100%' },
      }}>
      <DialogContent
        sx={(theme) => ({
          padding: '40px 56px 24px',
          [theme.breakpoints.down('sm')]: {
            padding: '20px 24px',
          },
        })}>
        <Typography fontSize={18} fontWeight={700} mb={3} textAlign="center">
          Certificações
        </Typography>
        <Grid
          container
          spacing={3}
          sx={(theme) => ({ padding: theme.spacing(2) })}>
          <Grid item xs={12}>
            <S.FieldTitle>Nome*</S.FieldTitle>
            <FormControl fullWidth variant="outlined">
              <TextField
                name={`certifications[${index}].name`}
                value={formik.values.certifications[index].name}
                onChange={formik.handleChange}
                placeholder="Ex: ESG no mercado financeiro"
                error={Boolean(getErrors<{ name: string }>('name', index))}
                helperText={getErrors<{ name: string }>('name', index)}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={8}>
            <S.FieldTitle>Emissor*</S.FieldTitle>
            <FormControl fullWidth variant="outlined">
              <TextField
                name={`certifications[${index}].issuer`}
                value={formik.values.certifications[index].issuer}
                onChange={formik.handleChange}
                placeholder="Ex: XP Academy"
                error={Boolean(getErrors<{ issuer: string }>('issuer', index))}
                helperText={getErrors<{ issuer: string }>('issuer', index)}
              />
            </FormControl>
          </Grid>
          <LocalizationProvider
            dateAdapter={AdapterDateFns}
            adapterLocale={ptBR}>
            <Grid item xs={12} md={4}>
              <S.FieldTitle>Emitida em*</S.FieldTitle>
              <FormControl fullWidth variant="outlined">
                <DatePicker
                  views={['month', 'year']}
                  format="MM/yyyy"
                  maxDate={new Date()}
                  slotProps={{
                    textField: {
                      name: `certifications[${index}].issuedAt`,
                      placeholder: 'MM/AAAA',
                      error: Boolean(
                        getErrors<{ issuedAt: string }>('issuedAt', index),
                      ),
                    },
                  }}
                  onChange={(value) => {
                    formik.setFieldValue(
                      `certifications[${index}].issuedAt`,
                      value && isValid(value)
                        ? format(value, 'yyyy-MM-dd')
                        : value,
                    );
                  }}
                  value={
                    formik.values.certifications?.[index]?.issuedAt
                      ? parseISO(formik.values.certifications[index].issuedAt)
                      : undefined
                  }
                />

                {Boolean(
                  getErrors<{ issuedAt: string }>('issuedAt', index),
                ) && (
                  <FormHelperText sx={{ color: '#EB5757' }}>
                    {getErrors<{ issuedAt: string }>('issuedAt', index)}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>
          </LocalizationProvider>
        </Grid>
      </DialogContent>
      <DialogActions
        sx={(theme) => ({
          gap: '28px',
          justifyContent: 'center',
          padding: '16px 84px 37px 72px',
          flexWrap: 'wrap',
          [theme.breakpoints.down('sm')]: {
            padding: '0 52px 40px 40px',
          },
          '& > button': {
            [theme.breakpoints.down('sm')]: {
              minWidth: '100%',
              marginLeft: '0 !important',
            },
          },
        })}>
        <Button variant="contained" onClick={handleSave}>
          Salvar
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          onClick={() => setOpenLeaveDialog(true)}>
          Cancelar
        </Button>
      </DialogActions>
      <ConfirmLeaveDialog
        title={`Tem certeza que deseja fechar a ${
          actionType === 'create' ? 'adição' : 'edição'
        } de certificação?`}
        open={openLeaveDialog}
        onClose={() => setOpenLeaveDialog(false)}
        handleAction={handleCancel}
        labelButton={actionType === 'create' ? 'Continuar' : 'Continuar edição'}
      />
    </Dialog>
  );
};

export default CertificationForm;
